import type { Logger } from '@/types/Logger';
import type {
  FileKeyHelpRequestor,
  FileKeysForRecipient,
  FileNeedingNewKey,
  MissingFileKeyHelpRequest,
} from '@/types/fileKey/FileKeyFacilitation';
import type { HeliaNode } from '@/types/poc/helia';
import type { AppServices, PocServices } from '@/types/services.types';
import type { AppDispatch, RootState } from '@/store';
import {
  clearRerunRequest,
  facilitationComplete,
  facilitationTotalCounted,
  fileKeyFailed,
  fileKeyProcessed,
  requestRerun,
  startFacilitation,
} from '@/store/slices/fileKey/fileKeyFacilitationSlice';
import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import { ensureDecapsPrivateKey } from '@/store/thunks/auth/ensureDecapsPrivateKey';
import { storeRecipientKeyBlob } from '@/store/thunks/fileKey/storeRecipientKeyBlob';
import type { FileKeyFacilitationService } from '@/services/abstract/FileKeyFacilitationService';
import { FileEncryptionService } from '@/services/abstract/poc/fileEncryption.service.interface';
import { normalizeCid } from '@/services/poc/cidUtils';
import { getErrorMessage } from '@/utils/error.utils';
import { packTar } from '@/utils/tar/packTar';
import { parseJsonArrayToBytes } from '@/utils/hex.utils';

const ENCRYPTION_FETCH_TIMEOUT_MS = 30_000;
const TAR_FILE_NAME = 'facilitated-file-keys.tar';
const TAR_MIME_TYPE = 'application/x-tar';

interface FacilitationContext {
  facilitatorPrivateKey: Uint8Array;
  currentUserId: string;
  dispatch: AppDispatch;
  getState: () => RootState;
  pocServices: PocServices;
  fileKeyFacilitationService: FileKeyFacilitationService;
  log: Logger;
}

/** The run context plus the Helia node resolved once per pass. */
interface PassContext extends FacilitationContext {
  helia: HeliaNode;
}

/** One re-encrypted key, held in memory until the pass's tar is packed. */
interface FacilitatedKeyEntry {
  requestorUserId: string;
  mlkemKeyVersion: string;
  sixId: string;
  fileId: string;
  newFileKeyCid: string;
  blobBytes: Uint8Array;
}

/** What survives past packing: only the tar CID and the recipients' file-to-CID pairs. */
interface PreparedBundle {
  tarCid: string;
  recipients: FileKeysForRecipient[];
  entryCount: number;
}

interface FacilitationResult {
  processed: number;
  total: number;
  errors: string[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isMissingFileKeyHelpRequestArray(value: unknown): value is MissingFileKeyHelpRequest[] {
  if (!Array.isArray(value)) {
    return false;
  }

  return value.every((entry) => {
    if (!isRecord(entry)) {
      return false;
    }

    const requestor = entry.requestor;
    return (
      isRecord(requestor) &&
      typeof requestor.userId === 'string' &&
      typeof requestor.encapsKey === 'string' &&
      typeof requestor.mlkemKeyVersion === 'string' &&
      Array.isArray(entry.filesNeedingNewFileKey) &&
      entry.filesNeedingNewFileKey.every(
        (file) =>
          isRecord(file) &&
          typeof file.fileId === 'string' &&
          typeof file.sixId === 'string' &&
          typeof file.facilitatorFileKeyCID === 'string'
      )
    );
  });
}

function readResult(getState: () => RootState): FacilitationResult {
  const { processedCount, totalCount, errors } = getState().fileKeyFacilitation;
  return { processed: processedCount, total: totalCount, errors };
}

/**
 * Re-encrypts every file key other members are missing and delivers them as one tar bundle
 * to HAP plus one batch command, regardless of file count.
 *
 * The run is claimed synchronously (`startFacilitation` before the first await) so the five
 * trigger sites cannot start overlapping runs. A trigger that lands mid-run sets
 * `rerunRequested`; the loop performs one more pass under the same claim. `status` stays
 * `running` from the claim until the single `facilitationComplete` in `finally`.
 */
export const facilitateFileKeys = createAppAsyncThunk(
  'fileKeyFacilitation/facilitate',
  async (_, { dispatch, getState, extra, rejectWithValue }) => {
    const log = extra.loggerService.createLogger({ namespace: 'six3ro:file-key-facilitation' });

    if (getState().fileKeyFacilitation.status === 'running') {
      log.info('Facilitation already running; rerun requested.');
      dispatch(requestRerun());
      return { processed: 0, total: 0, errors: [] } satisfies FacilitationResult;
    }
    dispatch(startFacilitation());

    try {
      const ctx = await buildFacilitationContext({ dispatch, getState, extra, log });
      await runUntilSettled(ctx);

      const result = readResult(getState);
      log.info('File key facilitation complete', result);
      return result;
    } catch (error) {
      // Only pre-loop failures reach here (no user, key unwrap); pass failures are caught
      // inside the loop. Every caller fires this fire-and-forget, so nothing surfaces to UI.
      const message = getErrorMessage(error);
      log.error('File key facilitation failed', error);
      dispatch(fileKeyFailed(message));
      return rejectWithValue({ message });
    } finally {
      dispatch(facilitationComplete());
    }
  }
);

interface BuildContextArgs {
  dispatch: AppDispatch;
  getState: () => RootState;
  extra: AppServices;
  log: Logger;
}

async function buildFacilitationContext(args: BuildContextArgs): Promise<FacilitationContext> {
  const { dispatch, getState, extra, log } = args;
  const { pocServices, userService, fileKeyFacilitationService } = extra;

  const currentUserId = getState().user.user?.id;
  if (currentUserId == null) {
    throw new Error('User not authenticated');
  }

  const facilitatorPrivateKey = await ensureDecapsPrivateKey({
    decapsKeyHex: getState().auth.decapsKey,
    ukekHex: getState().auth.ukek,
    userId: currentUserId,
    dispatch,
    userService,
    cryptoService: pocServices.cryptoService,
    log,
  });

  return {
    facilitatorPrivateKey,
    currentUserId,
    dispatch,
    getState,
    pocServices,
    fileKeyFacilitationService,
    log,
  };
}

/** Runs passes until no rerun is pending. A failing pass is recorded, never rethrown. */
async function runUntilSettled(ctx: FacilitationContext): Promise<void> {
  const { dispatch, getState, log } = ctx;

  while (true) {
    try {
      await runOnePass(ctx);
    } catch (error) {
      const message = `Facilitation pass failed: ${getErrorMessage(error)}`;
      log.error(message);
      dispatch(fileKeyFailed(message));
    }

    if (!getState().fileKeyFacilitation.rerunRequested) {
      return;
    }
    log.info('Rerun requested during the pass; running again.');
    dispatch(clearRerunRequest());
  }
}

/**
 * One pass: fetch the missing-keys list, re-encrypt everything in memory, then bundle and
 * submit once.
 */
async function runOnePass(ctx: FacilitationContext): Promise<void> {
  const { dispatch, pocServices, fileKeyFacilitationService, log } = ctx;

  const helia: HeliaNode | null = pocServices.p2pStackManagerService.getHeliaNode();
  if (helia === null) {
    throw new Error('Helia node not initialized');
  }

  log.info('Checking for missing file key requests...');
  const rawRequests = await fileKeyFacilitationService.getMissingFileKeyRequests();
  log.debug('Missing file key response received', rawRequests);

  if (!isMissingFileKeyHelpRequestArray(rawRequests)) {
    log.error('Missing file key response has unexpected shape', rawRequests);
    throw new Error('Invalid missing file key response shape');
  }

  const totalFiles = rawRequests.reduce(
    (sum, request) => sum + request.filesNeedingNewFileKey.length,
    0
  );
  dispatch(facilitationTotalCounted({ totalCount: totalFiles }));

  if (totalFiles === 0) {
    log.info('No missing file key requests. Nothing to facilitate.');
    return;
  }
  log.info(`Found ${rawRequests.length} requestor(s), ${totalFiles} file(s) to facilitate.`);

  const passContext: PassContext = { ...ctx, helia };
  const bundle = await prepareBundle(rawRequests, passContext);
  if (bundle === null) {
    return;
  }

  await uploadAndSubmitBundle(bundle, passContext);
}

async function reEncryptAllFileKeys(
  requests: MissingFileKeyHelpRequest[],
  ctx: PassContext
): Promise<FacilitatedKeyEntry[]> {
  const entries: FacilitatedKeyEntry[] = [];
  for (const request of requests) {
    entries.push(...(await reEncryptForRequestor(request, ctx)));
  }
  return entries;
}

/** Re-encrypts one requestor's files; a file that fails is recorded and skipped. */
async function reEncryptForRequestor(
  request: MissingFileKeyHelpRequest,
  ctx: PassContext
): Promise<FacilitatedKeyEntry[]> {
  const { requestor, filesNeedingNewFileKey } = request;
  ctx.log.debug(
    `Re-encrypting ${filesNeedingNewFileKey.length} file key(s) for requestor: ${requestor.userId}`
  );
  const recipientPublicKey = parseJsonArrayToBytes(requestor.encapsKey);

  const entries: FacilitatedKeyEntry[] = [];
  for (const file of filesNeedingNewFileKey) {
    try {
      entries.push(await reEncryptOneFileKey(file, recipientPublicKey, requestor, ctx));
      ctx.dispatch(fileKeyProcessed());
    } catch (error) {
      const message = `Failed for file ${file.fileId} (six: ${file.sixId}): ${getErrorMessage(error)}`;
      ctx.log.error(message);
      ctx.dispatch(fileKeyFailed(message));
    }
  }
  return entries;
}

async function reEncryptOneFileKey(
  file: FileNeedingNewKey,
  recipientPublicKey: Uint8Array,
  requestor: FileKeyHelpRequestor,
  ctx: PassContext
): Promise<FacilitatedKeyEntry> {
  const { facilitatorPrivateKey, currentUserId, pocServices, helia, log } = ctx;
  const { fileEncryptionService, fileStoreService } = pocServices;

  log.debug(`Fetching facilitator file key CID: ${file.facilitatorFileKeyCID}`);
  const encryptionJsonBytes = await fileStoreService.getFile(helia, file.facilitatorFileKeyCID, {
    signal: AbortSignal.timeout(ENCRYPTION_FETCH_TIMEOUT_MS),
  });
  const facilitatorEncryption = FileEncryptionService.parseRecipientKeyBlob(
    new TextDecoder().decode(new Uint8Array(encryptionJsonBytes)),
    currentUserId
  );

  const reEncrypted = await fileEncryptionService.reEncryptFileKeyForRecipient(
    facilitatorEncryption,
    facilitatorPrivateKey,
    recipientPublicKey
  );
  log.debug(`Re-encrypted file key for requestor: ${requestor.userId}, file: ${file.fileId}`);

  // One encoding feeds both the local store and the tar, so the CID HAP derives after
  // untarring is the same bytes' CID as `newFileKeyCid`.
  const blobBytes = new TextEncoder().encode(
    FileEncryptionService.serializeRecipientKeyBlob(reEncrypted)
  );
  const newFileKeyCid = await storeRecipientKeyBlob(
    helia,
    fileStoreService,
    `filekey-${file.fileId}-${requestor.userId}.json`,
    blobBytes
  );
  log.debug(`Stored new file key in Helia, CID: ${newFileKeyCid}, requestor: ${requestor.userId}`);

  return {
    requestorUserId: requestor.userId,
    mlkemKeyVersion: requestor.mlkemKeyVersion,
    sixId: file.sixId,
    fileId: file.fileId,
    newFileKeyCid,
    blobBytes,
  };
}

/**
 * Re-encrypts every requested key, packs the blobs into one tar, and stores the tar in local
 * Helia. The blob byte arrays live only inside this function so they can be collected during
 * the two long command polls that follow. Entry names carry the requestor so two requestors
 * needing the same file do not collide; HAP only needs the `.key.json` suffix.
 * Returns null when nothing could be re-encrypted.
 */
async function prepareBundle(
  requests: MissingFileKeyHelpRequest[],
  ctx: PassContext
): Promise<PreparedBundle | null> {
  const { pocServices, helia, log } = ctx;

  const entries = await reEncryptAllFileKeys(requests, ctx);
  if (entries.length === 0) {
    log.error('No file keys could be re-encrypted; nothing to bundle.');
    return null;
  }

  const tarBytes = await packTar(
    entries.map((entry) => ({
      name: `${entry.requestorUserId}/${entry.fileId}.key.json`,
      bytes: entry.blobBytes,
    }))
  );
  const tarFile = new File([tarBytes], TAR_FILE_NAME, { type: TAR_MIME_TYPE });
  const tarResult = await pocServices.fileStoreService.addFile(helia, tarFile);
  const tarCid = normalizeCid(tarResult.cid);

  const recipients = groupEntriesByRecipient(entries);
  log.info('Facilitation bundle stored in Helia', {
    entryCount: entries.length,
    requestorCount: recipients.length,
    tarBytes: tarBytes.byteLength,
    tarCid,
  });

  return { tarCid, recipients, entryCount: entries.length };
}

/** Hands the tar to HAP, then submits every recipient in one command. Each call polls to completion. */
async function uploadAndSubmitBundle(bundle: PreparedBundle, ctx: PassContext): Promise<void> {
  const { fileKeyFacilitationService, log } = ctx;
  const { tarCid, recipients, entryCount } = bundle;

  await fileKeyFacilitationService.uploadFileKeysBundle({ compressedUpdatedFileKeysCID: tarCid });
  log.info('Facilitation bundle accepted by HAP', { tarCid });

  await fileKeyFacilitationService.submitFileKeys({ recipients });
  log.info('Facilitated file keys submitted', { requestorCount: recipients.length, entryCount });
}

function groupEntriesByRecipient(entries: FacilitatedKeyEntry[]): FileKeysForRecipient[] {
  const byRequestor = new Map<string, FileKeysForRecipient>();

  for (const entry of entries) {
    let recipient = byRequestor.get(entry.requestorUserId);
    if (recipient === undefined) {
      recipient = {
        newFileKeysForUserId: entry.requestorUserId,
        mlkemKeyVersion: entry.mlkemKeyVersion,
        newFileKeys: [],
      };
      byRequestor.set(entry.requestorUserId, recipient);
    }
    recipient.newFileKeys.push({
      sixId: entry.sixId,
      newFileKeyFileId: entry.fileId,
      newFileKeyCID: entry.newFileKeyCid,
    });
  }

  return [...byRequestor.values()];
}
